import { type KeyboardEvent } from 'react'

import {
  Collapsible,
  Header,
  IconQuestionSymbol,
  InlineEditTextField,
  Space,
  Text,
} from '@procurify/ui'
import { type MessageDescriptor } from 'react-intl'
import styled from 'styled-components'

import { useProcurifyIntl } from '@webapp/hooks'
import {
  PDFLabelSections,
  type PDFLabelSectionEnum,
} from '@webapp/modules/vendor/entities/enums/PDFLabelSectionEnum' // eslint-disable-line import/no-internal-modules
import { type PDFLabelFieldMetadata } from '@webapp/modules/vendor/entities/PDFLabelSet' // eslint-disable-line import/no-internal-modules
import messages from './messages'

export interface IPOPDFLabelFieldRow {
  rowId: string
  fieldKey: string
  /** Read-only "Existing labels" column value (title-cased default). */
  existingLabel: string
  /** Current "Replace with" input value (draft ?? saved ?? default). */
  value: string
  maxLength: number
  isCustomFieldLabel: boolean
  translationType: PDFLabelFieldMetadata['translationType']
}

export interface IPOPDFLabelEditorSection {
  section: PDFLabelSectionEnum
  fields: IPOPDFLabelFieldRow[]
}

export interface IPOPDFLabelSectionEditorProps {
  sections: IPOPDFLabelEditorSection[]
  expandedSections: PDFLabelSectionEnum[]
  onToggleSection: (section: PDFLabelSectionEnum) => void
  onOverrideChange: (row: IPOPDFLabelFieldRow, value: string) => void
  onOverrideCommit: (row: IPOPDFLabelFieldRow) => void
}

const SECTION_TITLE_MESSAGES: Record<PDFLabelSectionEnum, MessageDescriptor> = {
  [PDFLabelSections.PO_FIELDS]: messages.poFieldsSectionTitle,
  [PDFLabelSections.PO_COSTS]: messages.poCostsSectionTitle,
  [PDFLabelSections.ITEM_FIELDS]: messages.itemFieldsSectionTitle,
}

const CF_SUB_SECTION_MESSAGES: Partial<
  Record<PDFLabelSectionEnum, MessageDescriptor>
> = {
  [PDFLabelSections.PO_FIELDS]: messages.poCustomFieldLabelsSubSectionTitle,
  [PDFLabelSections.ITEM_FIELDS]:
    messages.itemFieldsCustomFieldLabelsSubSectionTitle,
}

const SectionContainerStyled = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.content.secondaryBorder};
  border-radius: ${({ theme }) =>
    theme.box.borderRadius(theme.typography.htmlFontSize)};
  overflow: hidden;

  /* Collapsible renders its clickable header as .title — pad it to match
     the section header band. */
  .title {
    padding: ${({ theme }) => `${theme.space.small} ${theme.space.medium}`};
  }
`

const ColumnHeaderRowStyled = styled.div`
  display: grid;
  grid-template-columns: 2fr 3fr;
  padding: ${({ theme }) => `${theme.space.small} ${theme.space.medium}`};
  background: ${({ theme }) => theme.colors.content.secondaryBackground};
  border-top: 1px solid ${({ theme }) => theme.colors.content.secondaryBorder};
`

const FieldRowStyled = styled.div`
  display: grid;
  grid-template-columns: 2fr 3fr;
  align-items: center;
  padding: ${({ theme }) => `${theme.space.xsmall} ${theme.space.medium}`};
  border-top: 1px solid ${({ theme }) => theme.colors.content.secondaryBorder};
`

const SubSectionHeaderStyled = styled.div`
  padding: ${({ theme }) => `${theme.space.small} ${theme.space.medium}`};
  background: ${({ theme }) => theme.colors.content.secondaryBackground};
  border-top: 1px solid ${({ theme }) => theme.colors.content.secondaryBorder};
`

/**
 * Three collapsible PO PDF label sections with an "Existing labels" /
 * "Replace with" two-column body (BHV-033/034/035). Renders sections only —
 * the set selector, icon buttons, modals, and empty state are Feature Set
 * composition.
 */
export const POPDFLabelSectionEditor = ({
  sections,
  expandedSections,
  onToggleSection,
  onOverrideChange,
  onOverrideCommit,
}: IPOPDFLabelSectionEditorProps) => {
  const intl = useProcurifyIntl()

  const handleKeyDown = (e: KeyboardEvent, row: IPOPDFLabelFieldRow) => {
    if (e.key === 'Enter') {
      onOverrideCommit(row)
    }
  }

  const renderFieldRow = (row: IPOPDFLabelFieldRow) => (
    <FieldRowStyled key={row.rowId}>
      <Text>{row.existingLabel}</Text>
      <InlineEditTextField
        value={row.value}
        status='idle'
        maxLength={row.maxLength}
        ariaLabel={intl.formatMessage(messages.replaceWithInputAriaLabel, {
          label: row.existingLabel,
        })}
        onChange={(value) => onOverrideChange(row, value)}
        onBlur={() => onOverrideCommit(row)}
        onKeyDown={(e) => handleKeyDown(e, row)}
      />
    </FieldRowStyled>
  )

  return (
    <Space>
      {sections.map(({ section, fields }) => {
        const title = intl.formatMessage(SECTION_TITLE_MESSAGES[section])
        const cfSubSectionMessage = CF_SUB_SECTION_MESSAGES[section]
        const standardFields = fields.filter(
          (field) => !field.isCustomFieldLabel
        )
        const customFieldLabels = fields.filter(
          (field) => field.isCustomFieldLabel
        )

        return (
          <SectionContainerStyled key={section}>
            <Collapsible
              open={expandedSections.includes(section)}
              onChange={() => onToggleSection(section)}
              title={
                <Space
                  direction='horizontal'
                  justify='space-between'
                  align='center'
                >
                  <Header as='h5'>{title}</Header>
                  <IconQuestionSymbol
                    color='darkGrey'
                    ariaLabel={intl.formatMessage(
                      messages.sectionHelpAriaLabel,
                      { title }
                    )}
                  />
                </Space>
              }
            >
              <ColumnHeaderRowStyled>
                <Text bold>
                  {intl.formatMessage(messages.existingLabelsColumnHeader)}
                </Text>
                <Text bold>
                  {intl.formatMessage(messages.replaceWithColumnHeader)}
                </Text>
              </ColumnHeaderRowStyled>
              {standardFields.map(renderFieldRow)}

              {cfSubSectionMessage && customFieldLabels.length > 0 && (
                <>
                  <SubSectionHeaderStyled>
                    <Text bold>{intl.formatMessage(cfSubSectionMessage)}</Text>
                  </SubSectionHeaderStyled>
                  {customFieldLabels.map(renderFieldRow)}
                </>
              )}
            </Collapsible>
          </SectionContainerStyled>
        )
      })}
    </Space>
  )
}

POPDFLabelSectionEditor.displayName = 'POPDFLabelSectionEditor'
